import { supabase } from '../../../shared/lib/supabase';

// Define the PlayerAccess interface
export interface PlayerAccess {
  id: string;
  player_id: string;
  organisation_id: string;
  access_type: string;
  context: string | null;
  granted_by: string | null;
  granted_at: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  player_name?: string;
  player_position?: string;
  player_nationality?: string;
  player_kit_number?: number;
  organisation_name?: string;
}

export interface PlayerAccessCreateInput {
  player_id: string;
  organisation_id: string;
  access_type: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at: string;
  active: boolean;
}

export interface PlayerAccessUpdateInput {
  player_id?: string;
  access_type?: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at?: string;
  active?: boolean;
}

class PlayerAccessService {
  async getAll(): Promise<PlayerAccess[]> {
    try {
      console.log('🔍 Fetching player access with joins...');

      const { data, error } = await supabase
        .from('player_access')
        .select(`
          *,
          players!player_access_player_id_fkey(full_name, position, nationality, kit_number),
          organisations!player_access_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching player access:', error);
        throw error;
      }

      const transformed = (data || []).map((item: any) => ({
        ...item,
        player_name: item.players?.full_name || item.player_name,
        player_position: item.players?.position || null,
        player_nationality: item.players?.nationality || null,
        player_kit_number: item.players?.kit_number || null,
        organisation_name: item.organisations?.name || item.organisation_name
      }));

      console.log('✅ Player access fetched successfully:', transformed.length || 0, 'records');
      return transformed;
    } catch (error) {
      console.error('❌ Error in getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<PlayerAccess | null> {
    try {
      console.log('🔍 Fetching player access by ID:', id);

      const { data, error } = await supabase
        .from('player_access')
        .select(`
          *,
          players!player_access_player_id_fkey(full_name),
          organisations!player_access_organisation_id_fkey(name)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching player access by ID:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, player_name: (data as any).players?.full_name, organisation_name: (data as any).organisations?.name }
        : null;

      console.log('✅ Player access fetched successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in getById:', error);
      throw error;
    }
  }

  async create(input: PlayerAccessCreateInput): Promise<PlayerAccess> {
    try {
      console.log('🔍 Creating player access:', input);

      const { data, error } = await supabase
        .from('player_access')
        .insert([input])
        .select(`
          *,
          players!player_access_player_id_fkey(full_name),
          organisations!player_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error creating player access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, player_name: (data as any).players?.full_name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Player access created successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in create:', error);
      throw error;
    }
  }

  async update(id: string, input: PlayerAccessUpdateInput): Promise<PlayerAccess> {
    try {
      console.log('🔍 Updating player access:', id, input);

      const { data, error } = await supabase
        .from('player_access')
        .update(input)
        .eq('id', id)
        .select(`
          *,
          players!player_access_player_id_fkey(full_name),
          organisations!player_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error updating player access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, player_name: (data as any).players?.full_name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Player access updated successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting player access:', id);

      const { error } = await supabase
        .from('player_access')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting player access:', error);
        throw error;
      }

      console.log('✅ Player access deleted successfully');
    } catch (error) {
      console.error('❌ Error in delete:', error);
      throw error;
    }
  }
}

export const playerAccessService = new PlayerAccessService();
